export const THEME_STORAGE_KEY = "afrimed.theme";
export const LEGACY_THEME_STORAGE_KEYS = ["afrimed-theme", "theme"];
export const VALID_THEMES = ["system", "light", "dark"];

function userThemeStorageKey(userId = "") {
  const id = String(userId || "").trim();
  return id ? `${THEME_STORAGE_KEY}:user:${id}` : "";
}

export function readStoredTheme() {
  if (typeof window === "undefined") return "light";

  try {
    const stored = [THEME_STORAGE_KEY, ...LEGACY_THEME_STORAGE_KEYS]
      .map((key) => window.localStorage?.getItem(key))
      .find((value) => VALID_THEMES.includes(value));
    return stored || "light";
  } catch {
    return "light";
  }
}

export function resolveThemeMode(theme) {
  if (theme === "dark" || theme === "light") return theme;
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

export function persistTheme(theme) {
  if (typeof window === "undefined") return;

  try {
    [THEME_STORAGE_KEY, ...LEGACY_THEME_STORAGE_KEYS].forEach((key) =>
      window.localStorage?.setItem(key, theme)
    );
  } catch {
    // ignore storage issues
  }
}

export function readStoredThemeForUser(userId = "") {
  if (typeof window === "undefined") return "";
  const key = userThemeStorageKey(userId);
  if (!key) return "";

  try {
    const stored = window.localStorage?.getItem(key);
    return VALID_THEMES.includes(stored) ? stored : "";
  } catch {
    return "";
  }
}

export function persistThemeForUser(userId = "", theme = "") {
  if (typeof window === "undefined") return;
  const key = userThemeStorageKey(userId);
  if (!key || !VALID_THEMES.includes(theme)) return;

  try {
    window.localStorage?.setItem(key, theme);
  } catch {
    // ignore storage issues
  }
}

export function applyTheme(theme, { persist = true, dispatch = true } = {}) {
  const resolved = resolveThemeMode(theme);

  if (typeof document === "undefined") return resolved;

  const root = document.documentElement;
  if (!root) return resolved;

  if (persist) persistTheme(theme);

  root.setAttribute("data-theme", resolved);
  root.classList.toggle("dark", resolved === "dark");

  if (dispatch && typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("afrimed-theme-change", {
        detail: {
          theme,
          resolved,
        },
      })
    );
  }

  return resolved;
}

export function initTheme() {
  return applyTheme(readStoredTheme(), { persist: false, dispatch: false });
}

export function toggleTheme() {
  const order = ["light", "dark", "system"];
  const current = readStoredTheme();
  const next = order[(order.indexOf(current) + 1) % order.length];
  applyTheme(next);
  return next;
}
